import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ServiceStackModes, ServiceStackTypes, ServiceStackAutoscalingHAContainers, AutoscalingProfile } from '@zerops/models/service-stack';
import {
  ServiceStackCpuModes,
  ServiceStackHorizontalAutoscaling,
  ServiceStackVerticalAutoscaling
} from '@zerops/models/settings';
import { AdjustAutoscalingOutput } from './autoscaling-form-field.model';
import { AutoscalingSettingsFormComponent } from '@zerops/zui/autoscaling-settings-form';
import { CommonModule } from '@angular/common';
import { Modes } from './autoscaling-form.model';

@Component({
  selector: 'zui-autoscaling-form-field',
  templateUrl: './autoscaling-form-field.component.html',
  styleUrl: './autoscaling-form-field.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AutoscalingSettingsFormComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoscalingFormFieldComponent),
      multi: true
    }
  ]
})
export class AutoscalingFormFieldComponent implements ControlValueAccessor {

  modes = Modes;
  serviceStackTypes = ServiceStackTypes;

  // Input
  value = input<any>();
  supportsVerticalAutoscaling = input<boolean>(true);
  serviceStackType = input<ServiceStackTypes>();
  // `horizontalAutoscaling`
  defaultHorizontalAutoscaling = input<ServiceStackHorizontalAutoscaling>();
  // `verticalAutoscaling`
  defaultVerticalAutoscaling = input<ServiceStackVerticalAutoscaling>();
  verticalPlaceholder = input<ServiceStackVerticalAutoscaling>(undefined);
  horizontalPlaceholder = input<ServiceStackHorizontalAutoscaling>(undefined);
  serviceStackMode = input<ServiceStackModes>();
  mode = input<Modes>(Modes.Add);
  // Available modes from settings API (e.g. ['ha', 'single'])
  modeList = input<string[]>([]);
  // HA container configuration
  haContainers = input<ServiceStackAutoscalingHAContainers>();
  // Autoscaling profiles from settings API
  autoscalingProfiles = input<AutoscalingProfile[]>([]);
  // Free users get the follow-up reapply hint in the over-limit banner
  isFreeUser = input<boolean>(false);

  adjustScaling = output<AdjustAutoscalingOutput>();

  // # Validation
  isFormValid = signal<boolean>(true);

  // # Control Value Accessor
  valueRef = signal<any>({});

  writeValue(v: any) {
    this.valueRef.set(v);
  }

  registerOnChange(fn: any) {
    this._propagateChange = fn;
  }

  registerOnTouched(_: any) {
    return;
  }

  private _propagateChange = (_: any) => {
    return;
  };

  // Helper method to get current value
  #getCurrentValue() {
    return this.valueRef();
  }

  // Helper method to update value
  #updateValue(newValue: any) {
    const updatedValue = { ...(this.#getCurrentValue() || {}), ...(newValue || {}) };
    this.valueRef.set(updatedValue);
    this._propagateChange(updatedValue);
  }

  // Effect to sync external value input with internal value
  constructor() {
    effect(() => {
      const inputValue = this.value();
      if (inputValue !== undefined) {
        const currentInternal = untracked(() => this.valueRef());
        this.valueRef.set({ ...(currentInternal || {}), ...(inputValue || {}) });
      }
    }, { allowSignalWrites: true });
  }

  // # Methods

  // Called by inline autoscaling-settings-form when value changes
  onInlineValueChange(newValue: any) {
    this.#updateValue(newValue);
  }

  setCpuMode(cpuMode: ServiceStackCpuModes, emit = false) {
    this.#updateValue({
      verticalAutoscaling: { cpuMode }
    });

    if (emit) {
      this.adjustScaling.emit(this.#getCurrentValue());
    }
  }

  onCpuModeChange(mode: string) {
    this.setCpuMode(mode as ServiceStackCpuModes, true);
  }


  updateValue(newValue: any) {
    this.#updateValue(newValue);
  }

}
